import { FileType } from '#enums';
import { AttachmentFile, Dtos } from '#models';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'];

const MIME_MAP: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
};

// Known document extensions are whatever MIME_MAP defines that isn't an image extension
const DOCUMENT_EXTENSIONS = Object.keys(MIME_MAP).filter(ext => !IMAGE_EXTENSIONS.includes(ext));

/** Detect whether a file is an image or document based on its extension; unrecognized extensions default to image. */
export function detectFileType(fileName: string): FileType {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  return DOCUMENT_EXTENSIONS.includes(ext) ? FileType.Document : FileType.Image;
}

/**
 * Build a usable URL from a raw photo string (base64 or already a URL).
 * Returns the original string unchanged if it is already a data URI or HTTP URL.
 * Returns null when photo is empty.
 */
export function buildFileUrl(photo: string | null | undefined, fileName: string): string | null {
  if (!photo) return null;
  if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  const mime = MIME_MAP[ext] ?? 'application/octet-stream';
  return `data:${mime};base64,${photo}`;
}

/** Map a ResponseItemImage to an AttachmentFile, using the shared helpers above. */
export function mapImageToAttachmentFile(img: Dtos.ResponseItemImage, uploadedBy?: string | null): AttachmentFile {
  const fileName = img.Caption || 'File';
  const fileType = detectFileType(fileName);
  const fileUrl = buildFileUrl(img.Photo, fileName) ?? '';
  return {
    id: img.Id,
    file: fileUrl,
    fileType,
    guid: img.GUID,
    fileName,
    createDate: new Date(img.CreateDate),
    uploadedBy: uploadedBy ?? undefined,
    contentType: null,
    imageURL: fileType === FileType.Image ? fileUrl : undefined,
  };
}
